import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from "../common/config";
import RotatingRisquiKey from "./RotatingRisquiKey";
import AuthService from "../services/auth.service";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import LanguageSwitcher from "../common/LanguageSwitcher";

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const [rotation, setRotation] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [newEnvironment, setNewEnvironment] = useState("");

  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const autoLoginToken = queryParams.get('autoLoginToken');
    if (autoLoginToken) {
      AuthService.autoLogin(autoLoginToken)
        .then((data) => {
          // Remove autoLoginToken from URL to prevent re-triggering
          window.history.replaceState({}, document.title, window.location.pathname);
          window.location.reload();
        })
        .catch((err) => {
          console.error("Auto login failed", err);
        });
    }

    if (localStorage.getItem("preferredEnvironment") && window.location.search.includes("redirected=true")) {
      localStorage.removeItem("preferredEnvironment");
    }

    if (localStorage.getItem("preferredEnvironment")) {
      window.location.href = 'https://' + localStorage.getItem("preferredEnvironment") + '.risqui.nl/?redirected=true';
    }
  }, []);

  const setForgotPassword = (e) => {
    e.preventDefault();
    setMessage("");
    setForgotPasswordMode(true);
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    AuthService.forgotPassword(email, i18n.language).then(
      (response) => {
        setMessage(response.data?.msg || t('login_page.reset_password_success'));
        setLoading(false);
      },
      (error) => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.msg) ||
          error.msg ||
          error.toString();
        setMessage(resMessage);
        setLoading(false);
      }
    );
  };

  const onChangeEmail = (e) => {
    onChangeRotation(e);
    setEmail(e.target.value);
  };

  const onChangePassword = (e) => {
    onChangeRotation(e);
    setPassword(e.target.value);
  };

  const onChangeRotation = (e) => {
    const formVal = e.target.value;
    setRotation(Math.max(-15, 80 - formVal.length * 5));
  };

  const openModal = () => {
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleEnvironmentChange = (e) => {
    e.preventDefault();
    let env = newEnvironment.replace(".risqui.nl", "");

    if (env) {
      localStorage.setItem("preferredEnvironment", env);
      window.location.href = 'https://' + env + '.risqui.nl/?redirected=true';
    }

    closeModal();
  };

  const handleLogin = (e) => {
    e.preventDefault();

    setMessage("");
    setLoading(true);

    AuthService.login(email, password, twoFactorToken).then(
      () => {
        window.location.reload();
      },
      (error) => {
        setLoading(false);

        if (error.response && error.response.status === 401) {
          setTwoFactorRequired(true);
          setMessage(t('auth_errors.2fa_required'));
        } else {
          // Check if server sent a translation key
          const msgKey = error.response?.data?.msgKey;
          if (msgKey) {
            setMessage(t(msgKey));
          } else {
            // Fallback to old message format for backward compatibility
            const resMessage =
              (error.response &&
                error.response.data &&
                error.response.data.msg) ||
              error.msg ||
              error.toString();

            switch (error.response?.status) {
              case 400:
                setMessage(error.response.data.msg ? "Error logging in: " + error.response.data.msg : t('auth_errors.invalid_credentials'));
                break;
              case 429:
                setMessage(t('auth_errors.too_many_attempts'));
                break;
              default:
                setMessage(resMessage);
                break;
            }
          }
        }
      }
    );
  };

  const handleMicrosoftLogin = () => {
    window.location.href = API_BASE_URL + '/api/auth/microsoft';
  };

  return (
    <div id="login-container">
      <div className="container">
        <div className="col-md-12">
          <div id="login-content">
            <div id="login-logo" className="pointer-class" onClick={() => window.location.href = 'https://risqui.nl'}>
              <i className="icon-risui" />
              <RotatingRisquiKey rotation={rotation} />
            </div>

            <div className="card card-container">
              <LanguageSwitcher />
              <div className="profile-img-card" />
              <h1>{forgotPasswordMode ? t('login_page.reset_password') : t('login_page.login')}</h1>
              <h3>{forgotPasswordMode ? t('login_page.reset_password_description') : t('login_page.welcome_message')}</h3>

              {message && (
                <div className="form-group">
                  <div className={"alert alert-" + (message.includes("2FA") || message.includes("instructi") ? "warning" : "danger")} role="alert">
                    {message}
                  </div>
                </div>
              )}

              {!forgotPasswordMode && (
                <>
                  <div className="form-group">
                    <button type="button" className="btn btn-outline-primary btn-block login-submit login-microsoft" onClick={handleMicrosoftLogin}>
                      <span className="microsoft-logo" />{t('login_page.sign_in_with_microsoft')}
                    </button>
                  </div>

                  <span className="hr-text">{t('login_page.or')}</span>

                </>
              )}


              <form onSubmit={forgotPasswordMode ? handleForgotPassword : handleLogin}>
                {(window.location.search.includes("redirected=true") || window.location.hostname === "main.risqui.nl") && (
                  <div className="form-group">
                    <label htmlFor="email">{t('login_page.environment')} (<a href="#" onClick={openModal} className="underline">{t('login_page.change')}</a>)</label>
                    <input
                      type="input"
                      className="form-control"
                      name="environment"
                      placeholder="environment.risqui.nl"
                      value={window.location.hostname.split(".")[0]}
                      disabled
                    />
                  </div>
                )}

                {(!forgotPasswordMode || !message) && (
                  <div className="form-group">
                    <label htmlFor="email">{t('login_page.email')}</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      placeholder="name@email.com"
                      value={email}
                      onChange={onChangeEmail}
                      onBlur={onChangeRotation}
                      onFocus={onChangeRotation}
                      autoComplete="current-username"
                      required
                    />
                  </div>
                )}

                {!forgotPasswordMode && (
                  <>
                    <div className="form-group">
                      <label htmlFor="password">{t('login_page.password')}</label>
                      <input
                        type="password"
                        className="form-control"
                        name="password"
                        placeholder="*********"
                        value={password}
                        onChange={onChangePassword}
                        onBlur={onChangeRotation}
                        onFocus={onChangeRotation}
                        autoComplete="current-password"
                        required
                      />
                    </div>

                    {twoFactorRequired && (
                      <div className="form-group">
                        <label htmlFor="twoFactorToken">{t('login_page.2fa_token')}</label>
                        <input
                          type="text"
                          className="form-control"
                          name="twoFactorToken"
                          placeholder={t('login_page.enter_2fa_token')}
                          value={twoFactorToken}
                          onChange={(e) => setTwoFactorToken(e.target.value)}
                          required
                        />
                      </div>
                    )}
                  </>
                )}

                {!forgotPasswordMode && (
                  <div className="form-check">
                    <div style={{float: 'left'}}>
                      <input type="checkbox" className="form-check-input" id="rememberMe" />
                      {t('login_page.remember_me')}
                    </div>

                    <div style={{float: 'right', textDecoration: 'underline'}}>
                      <a href="#" onClick={setForgotPassword}>{t('login_page.forgot_password')}</a>
                    </div>
                  </div>
                )}

                <div className="form-group" style={{display: forgotPasswordMode && message ? "none" : "block"}}>
                  <button className="btn btn-outline-primary btn-block login-submit" disabled={loading}>
                    {loading && (
                      <span className="spinner-border spinner-border-sm" />
                    )}
                    <span>{forgotPasswordMode ? t('login_page.reset_password_button') : t('login_page.login_button')}</span>
                  </button>
                </div>

                {forgotPasswordMode && (
                  <div className="form-group" style={{textAlign: 'center', marginTop: '15px'}}>
                    <a href="#" onClick={(e) => {e.preventDefault(); setForgotPasswordMode(false); setMessage("");}} style={{textDecoration: 'underline'}}>
                      {t('login_page.back_to_login')}
                    </a>
                  </div>
                )}
              </form>

            </div>
          </div>
        </div>
      </div>

      <Modal show={modalIsOpen} onHide={closeModal}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t('login_page.change_environment')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label htmlFor="newEnvironment">{t('login_page.environment')}</label>
            <input
              type="text"
              className="form-control"
              id="newEnvironment"
              placeholder="environment.risqui.nl"
              value={newEnvironment}
              autoComplete="off"
              onChange={(e) => setNewEnvironment(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>

          <div className="white-border-btn cancel-btn" onClick={closeModal}>{t('login_page.cancel')}</div>
          <div style={{flexGrow: 1}} />
          <div className="white-border-btn submit-btn" onClick={handleEnvironmentChange}>{t('login_page.change')}</div>

        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LoginPage;